import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'
import classNames from 'classnames'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import Select from '@/components/forms/Select'
import { toast } from '@/components/toasts/toast.helpers'
import { copyFieldDescription } from '@/reducers/playbooks/playbook.actions'

const ALL_PLAYBOOKS = '__all_playbooks__'
const ALL_PLAYBOOKS_OPTION = { value: ALL_PLAYBOOKS, label: 'All playbooks' }

// Copies a field's saved description from the loaded playbook to one or more
// other playbooks (or all of them) that share the field. The API reports
// per-playbook outcomes, which we group into updated / skipped / errored.
export const CopyFieldDescriptionModal = ({
  show,
  onClose,
  fieldName,
  fieldId,
  sourceFlowUuid,
  playbookOptions,
}) => {
  const dispatch = useDispatch()
  // Array of selected react-select option objects. Defaults to "All playbooks".
  const [selected, setSelected] = useState([ALL_PLAYBOOKS_OPTION])
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  const targetOptions = [ALL_PLAYBOOKS_OPTION, ...playbookOptions]
  const copyToAllPlaybooks = selected.some((option) => option.value === ALL_PLAYBOOKS)

  const playbookNameForCid = (cid) =>
    playbookOptions.find((option) => option.value === cid)?.label || cid

  // "All playbooks" and specific playbooks are mutually exclusive: picking All
  // collapses to just All; picking a specific one drops All. react-select
  // appends newly selected options, so the last entry is the latest pick.
  const handleSelectChange = (options) => {
    const next = options || []
    const justPickedAll = !isEmpty(next) && next[next.length - 1].value === ALL_PLAYBOOKS
    if (justPickedAll) {
      setSelected([ALL_PLAYBOOKS_OPTION])
    } else {
      setSelected(next.filter((option) => option.value !== ALL_PLAYBOOKS))
    }
  }

  const handleClose = () => {
    setResult(null)
    setSelected([ALL_PLAYBOOKS_OPTION])
    setSubmitting(false)
    onClose()
  }

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      const copyResult = await dispatch(
        copyFieldDescription({
          sourceFlowUuid,
          fieldId,
          targetPlaybookCids: copyToAllPlaybooks ? null : selected.map((option) => option.value),
          copyToAllPlaybooks,
        })
      )
      setResult(copyResult)
    } catch (err) {
      console.error(err)
      toast.error('Failed to copy description', { cause: err })
    } finally {
      setSubmitting(false)
    }
  }

  const renderGroup = (label, cids, className) => {
    if (isEmpty(cids)) {
      return null
    }
    return (
      <div className={classNames('copy-result-group', className)}>
        <h4>{label}</h4>
        <ul>
          {cids.map((cid) => (
            <li key={cid}>{playbookNameForCid(cid)}</li>
          ))}
        </ul>
      </div>
    )
  }

  const nothingAffected =
    result && isEmpty(result.updated) && isEmpty(result.skipped) && isEmpty(result.errored)

  return (
    <BasicModal
      show={show}
      onClose={handleClose}
      title={`Copy "${fieldName}" description`}
      size="tiny"
    >
      {result ? (
        <>
          {nothingAffected ? (
            <p className="muted-text">No playbooks were affected.</p>
          ) : (
            <div className="copy-result" data-testid="copy-result">
              {renderGroup('Updated', result.updated, 'copy-result-group--updated')}
              {renderGroup(
                'Skipped (field not found)',
                result.skipped,
                'copy-result-group--skipped'
              )}
              {renderGroup('Failed', result.errored, 'copy-result-group--errored')}
            </div>
          )}
          <div className="modal-footer">
            <Button primary type="button" data-testid="copy-done-button" onClick={handleClose}>
              Done
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="muted-text">
            Copies this field&apos;s last saved description to the selected playbooks, or to every
            playbook that has the same field. Playbooks without this field are skipped.
          </p>
          <div data-testid="copy-target-select">
            <Select
              isMulti
              aria-label="Target playbooks"
              options={targetOptions}
              value={selected}
              onChange={handleSelectChange}
              placeholder="Select playbooks"
              fixedWidth
            />
          </div>
          <div className="modal-footer">
            <Button
              type="button"
              data-testid="copy-cancel-button"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              primary
              type="button"
              data-testid="copy-confirm-button"
              onClick={handleConfirm}
              loading={submitting}
              disabled={submitting || isEmpty(selected)}
            >
              Copy Description
            </Button>
          </div>
        </>
      )}
    </BasicModal>
  )
}
